import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Busy, Field, Metric, PageHeader, Pager, Panel, StatusBadge } from '@/components/kit';
import { PickSelect, Pill, SwitchRow } from '@/components/inputs';
import { BackLink } from '@/components/record';
import DataTable, { Dash } from '@/components/admin/DataTable';
import {
  ACCEPT,
  COLUMN_GROUPS,
  DEMO_HEADERS,
  DEMO_ROWS,
  IMPORT_COLUMNS,
  MAX_IMPORT_ROWS,
  autoMap,
  chunk,
  customerKey,
  downloadSampleCsv,
  downloadSampleXlsx,
  downloadTemplateCsv,
  downloadTemplateXlsx,
  readSpreadsheet,
  rowToCustomer,
  rowToOrder,
} from '@/shared/importer';
import { ledgerName } from '@/shared/ledger';
import { costForCredits, useCreditRates } from '@/shared/creditRates';
import { money, paymentLabel, planName, termLabel } from '@/shared/pricing';
import { logActivity, todayDate } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { isIn } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/orders/import', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

const MAX_BYTES = 10 * 1024 * 1024;
const WRITE_BATCH = 25;
const LOOKUP_BATCH = 100;
const PAGE_SIZE = 25;
const NONE = 'none';

const RESULT_STYLES = {
  Ready: 'border-success-border bg-success-muted text-success',
  Update: 'border-highlight/40 bg-highlight/15 text-highlight',
  Skip: 'border-border bg-muted text-muted-foreground',
  Problem: 'border-destructive/40 bg-destructive/10 text-destructive',
};

const VIEWS = ['all', 'Ready', 'Duplicate', 'Problem'];
const VIEW_LABELS = { all: 'All rows', Ready: 'Ready', Duplicate: 'Already here', Problem: 'Problems' };

const DUP_MODES = ['skip', 'update'];
const DUP_LABELS = { skip: 'Skip them', update: 'Update the existing order' };

const EMPTY_EXISTING = { byKey: {}, byCode: {}, customers: {} };

/**
 * Customers that already exist, keyed like customerKey(): by email, and by name for clients with no email.
 * When two customers share a name, the newest wins.
 */
async function findCustomers(payloads) {
  const out = {};
  const emails = [
    ...new Set(
      payloads
        .map((p) => String(p.email_c ?? '').trim())
        .filter(Boolean)
        .flatMap((e) => [e, e.toLowerCase()]),
    ),
  ];
  const names = [
    ...new Set(payloads.filter((p) => !p.email_c && p.full_name_c).map((p) => String(p.full_name_c).trim())),
  ];
  for (const part of chunk(emails, LOOKUP_BATCH)) {
    const res = await sdk
      .table('customers_c')
      .select(['Name', 'email_c'])
      .where(isIn('email_c', part))
      .orderByDesc('Id')
      .limit(LOOKUP_BATCH * 2)
      .fetch();
    (res?.data ?? []).forEach((c) => {
      const key = customerKey({ email_c: c.email_c });
      if (key && !out[key]) out[key] = c.Id;
    });
  }
  for (const part of chunk(names, LOOKUP_BATCH)) {
    const res = await sdk
      .table('customers_c')
      .select(['Name', 'email_c'])
      .where(isIn('Name', part))
      .orderByDesc('Id')
      .limit(LOOKUP_BATCH * 2)
      .fetch();
    (res?.data ?? []).forEach((c) => {
      const key = customerKey({ full_name_c: c.Name });
      if (key && !out[key]) out[key] = c.Id;
    });
  }
  return out;
}

/** Look up orders and customers that already exist for the file's usernames, codes, emails and names. */
async function findExisting(prepared) {
  const usernames = [...new Set(prepared.map((r) => r.payload.line_username_c).filter(Boolean))];
  const codes = [...new Set(prepared.filter((r) => r.codeGiven).map((r) => r.payload.Name))];
  const out = { byKey: {}, byCode: {}, customers: {} };

  for (const part of chunk(usernames, LOOKUP_BATCH)) {
    const res = await sdk
      .table('orders_c')
      .select(['Name', 'line_username_c'])
      .where(isIn('line_username_c', part))
      .limit(LOOKUP_BATCH * 2)
      .fetch();
    (res?.data ?? []).forEach((o) => {
      if (o.line_username_c) out.byKey[String(o.line_username_c).toLowerCase()] = { Id: o.Id, Name: o.Name };
    });
  }
  for (const part of chunk(codes, LOOKUP_BATCH)) {
    const res = await sdk.table('orders_c').select(['Name']).where(isIn('Name', part)).limit(LOOKUP_BATCH).fetch();
    (res?.data ?? []).forEach((o) => {
      out.byCode[o.Name] = { Id: o.Id, Name: o.Name };
    });
  }
  out.customers = await findCustomers(prepared.map((r) => r.payload));
  return out;
}

/** Only the fields that carry a value, so an update never blanks what is already on the order. */
function filled(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([k, v]) => k !== 'Name' && v !== null && v !== '' && v !== false),
  );
}

function ColumnSelect({ id, value, headers, sample, onChange }) {
  return (
    <Select value={value === undefined || value === null ? NONE : String(value)} onValueChange={(v) => onChange(v === NONE ? undefined : Number(v))}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue placeholder="Not in file" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>Not in file</SelectItem>
        {headers.map((h, i) => (
          <SelectItem key={`${h}-${i}`} value={String(i)}>
            {h || `Column ${i + 1}`}
            {sample?.[i] !== undefined && String(sample[i]).trim() !== '' ? ` · ${String(sample[i]).slice(0, 24)}` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function ImportOrders() {
  const inputRef = useRef(null);
  const [source, setSource] = useState(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [map, setMap] = useState({});
  const [reading, setReading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [server, setServer] = useState('');
  const [dupMode, setDupMode] = useState('skip');
  const [withPayments, setWithPayments] = useState(true);
  const [withLedger, setWithLedger] = useState(true);
  const [withCustomers, setWithCustomers] = useState(true);
  const [existing, setExisting] = useState(EMPTY_EXISTING);
  const [checking, setChecking] = useState(false);
  const [view, setView] = useState('all');
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const { rates } = useCreditRates();

  const sheet = source?.sheets?.[sheetIndex] ?? null;
  const headers = sheet?.headers ?? [];
  const sheetRows = sheet?.rows ?? [];

  useEffect(() => {
    setMap(autoMap(headers));
  }, [source, sheetIndex]);

  const prepared = useMemo(
    () => sheetRows.slice(0, MAX_IMPORT_ROWS).map((r, i) => ({ Id: i + 1, line: i + 2, ...rowToOrder(r, map, { server: server.trim() }) })),
    [sheetRows, map, server],
  );

  const lookupKey = useMemo(
    () =>
      prepared
        .map((r) => `${r.payload.line_username_c ?? ''}|${r.codeGiven ? r.payload.Name : ''}|${customerKey(r.payload) ?? ''}`)
        .join(','),
    [prepared],
  );

  useEffect(() => {
    if (!prepared.length) {
      setExisting(EMPTY_EXISTING);
      return undefined;
    }
    let live = true;
    setChecking(true);
    findExisting(prepared)
      .then((found) => {
        if (live) setExisting(found);
      })
      .catch(() => {
        if (live) toast.error('Could not check for orders that are already here');
      })
      .finally(() => {
        if (live) setChecking(false);
      });
    return () => {
      live = false;
    };
  }, [lookupKey]);

  const classified = useMemo(() => {
    const seen = {};
    return prepared.map((r) => {
      const problems = [...r.problems];
      if (r.key) {
        if (seen[r.key]) problems.push(`Same line username as row ${seen[r.key]}`);
        else seen[r.key] = r.line;
      }
      const match = (r.key && existing.byKey[r.key]) || (r.codeGiven && existing.byCode[r.payload.Name]) || null;
      const ckey = customerKey(r.payload);
      const customer = ckey ? existing.customers[ckey] ?? null : null;
      let state = 'Ready';
      if (problems.length) state = 'Problem';
      else if (match) state = 'Duplicate';
      const action = state === 'Problem' ? 'Problem' : state === 'Duplicate' ? (dupMode === 'update' ? 'Update' : 'Skip') : 'Ready';
      return { ...r, problems, match, ckey, customer, state, action };
    });
  }, [prepared, existing, dupMode]);

  const counts = useMemo(() => {
    const c = { all: classified.length, Ready: 0, Duplicate: 0, Problem: 0 };
    classified.forEach((r) => {
      c[r.state] += 1;
    });
    return c;
  }, [classified]);

  const visible = view === 'all' ? classified : classified.filter((r) => r.state === view);
  const pageRows = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [view, source, sheetIndex]);

  const missingRequired = IMPORT_COLUMNS.filter((c) => c.required && map[c.key] === undefined);
  const toCreate = classified.filter((r) => r.action === 'Ready');
  const toUpdate = classified.filter((r) => r.action === 'Update');
  const writes = toCreate.length + toUpdate.length;
  const newCustomers = useMemo(() => {
    if (!withCustomers) return [];
    const seen = new Map();
    [...toCreate, ...toUpdate].forEach((r) => {
      if (r.ckey && !r.customer && !seen.has(r.ckey)) seen.set(r.ckey, r.payload);
    });
    return [...seen.entries()].map(([key, payload]) => ({ key, payload }));
  }, [classified, withCustomers]);
  const newCustomerKeys = useMemo(() => new Set(newCustomers.map((c) => c.key)), [newCustomers]);
  const unused = headers.filter((h, i) => !Object.values(map).includes(i));
  const matched = Object.keys(map).length;

  async function openFile(file) {
    if (!file) return;
    if (!/\.(csv|xls|xlsx|txt)$/i.test(file.name)) {
      toast.error('Pick a .csv, .xls or .xlsx file');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('That file is over 10 MB. Split it and import in parts.');
      return;
    }
    setReading(true);
    setResult(null);
    try {
      const { sheets } = await readSpreadsheet(file);
      if (!sheets.length || !sheets.some((s) => s.rows.length)) {
        if (sheets.some((s) => s.headers.length)) {
          toast.error('That file has headers but no clients yet', { description: 'Fill in one client per row under the headers, save, and drop it here again.' });
        } else {
          toast.error('No rows found in that file');
        }
        return;
      }
      const first = Math.max(0, sheets.findIndex((s) => s.rows.length));
      setSource({ name: file.name, demo: false, sheets });
      setSheetIndex(first);
      setView('all');
      const total = sheets[first].rows.length;
      if (total > MAX_IMPORT_ROWS) toast.warning(`Only the first ${MAX_IMPORT_ROWS} of ${total} rows will be imported`);
    } catch (err) {
      toast.error('Could not read that file', { description: err?.message });
    } finally {
      setReading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function loadDemo() {
    setResult(null);
    setSource({ name: 'Demo client list', demo: true, sheets: [{ name: 'Demo', headers: DEMO_HEADERS, rows: DEMO_ROWS }] });
    setSheetIndex(0);
    setView('all');
  }

  function reset() {
    setSource(null);
    setResult(null);
    setMap({});
    setView('all');
    setProgress({ done: 0, total: 0 });
  }

  async function templateXlsx() {
    try {
      await downloadTemplateXlsx();
      toast.success('Template downloaded', { description: 'Fill in the Clients sheet, save, and drop it here.' });
    } catch {
      toast.error('Could not build the Excel template. Try the CSV template instead.');
    }
  }

  async function sampleXlsx() {
    try {
      await downloadSampleXlsx();
    } catch {
      toast.error('Could not build the Excel sample. The CSV one works the same.');
    }
  }

  async function runImport() {
    if (!writes) return;
    setImporting(true);
    const total = writes + newCustomers.length;
    let done = 0;
    const errors = [];
    let customers = 0;
    let created = 0;
    let updated = 0;
    let payments = 0;
    let entries = 0;
    setProgress({ done: 0, total });
    try {
      const customerIds = { ...existing.customers };
      if (newCustomers.length) {
        const note = `Added by import from ${source?.name ?? 'a file'}`;
        for (const part of chunk(newCustomers, WRITE_BATCH)) {
          const res = await sdk.table('customers_c').create(part.map((c) => rowToCustomer(c.payload, note)));
          customers += res?.data?.length ?? 0;
          (res?.messages ?? []).forEach((m) => errors.push(`Customer: ${m}`));
          done += part.length;
          setProgress({ done, total });
        }
        if (customers) Object.assign(customerIds, await findCustomers(newCustomers.map((c) => c.payload)));
      }
      const customerFor = (r) => r.customer || (r.ckey ? customerIds[r.ckey] : null) || null;

      for (const part of chunk(toCreate, WRITE_BATCH)) {
        const body = part.map((r) => {
          const id = customerFor(r);
          return { ...r.payload, ...(id ? { customer_c: id } : {}) };
        });
        const res = await sdk.table('orders_c').create(body);
        created += res?.data?.length ?? 0;
        (res?.messages ?? []).forEach((m) => errors.push(m));
        done += part.length;
        setProgress({ done, total });
      }

      for (const r of toUpdate) {
        const id = customerFor(r);
        const res = await sdk.table('orders_c').update({ Id: r.match.Id, ...filled(r.payload), ...(id ? { customer_c: id } : {}) });
        if (res?.data?.length) updated += 1;
        else errors.push(`${r.match.Name}: ${(res?.messages ?? [])[0] || 'not updated'}`);
        done += 1;
        setProgress({ done, total });
      }

      const ids = {};
      if (created && (withPayments || withLedger)) {
        for (const part of chunk(toCreate.map((r) => r.payload.Name), LOOKUP_BATCH)) {
          const res = await sdk.table('orders_c').select(['Name']).where(isIn('Name', part)).limit(LOOKUP_BATCH).fetch();
          (res?.data ?? []).forEach((o) => {
            ids[o.Name] = o.Id;
          });
        }
      }
      const saved = toCreate.filter((r) => ids[r.payload.Name]);

      if (withPayments) {
        const rows = saved
          .filter((r) => r.paid && Number(r.payload.price_c) > 0)
          .map((r) => ({
            Name: `${r.payload.Name} · New`,
            order_c: ids[r.payload.Name],
            plan_c: r.payload.plan_c,
            kind_c: 'New',
            amount_c: Number(r.payload.price_c),
            method_c: r.payload.payment_method_c || 'Card',
            paid_on_c: r.start || todayDate(),
          }));
        for (const part of chunk(rows, WRITE_BATCH)) {
          const res = await sdk.table('payments_c').create(part);
          payments += res?.data?.length ?? 0;
          (res?.messages ?? []).forEach((m) => errors.push(`Payment: ${m}`));
        }
      }

      if (withLedger) {
        const costOf = (r) => (r.cost !== null && r.cost !== undefined ? r.cost : costForCredits(rates, r.payload.plan_c, r.credits));
        const rows = saved.map((r) => ({
          Name: ledgerName({ kind: 'Activation', orderName: r.payload.Name }),
          entry_date_c: r.start || todayDate(),
          kind_c: 'Activation',
          order_c: ids[r.payload.Name],
          ...(r.paid && Number(r.payload.price_c) > 0 ? { collected_c: Number(r.payload.price_c) } : {}),
          ...(r.credits !== null ? { credits_c: r.credits } : {}),
          ...(r.payload.plan_c ? { service_c: r.payload.plan_c } : {}),
          ...(costOf(r) !== null ? { cost_c: costOf(r) } : {}),
          notes_c: `Imported from ${source?.name ?? 'a file'}`,
        }));
        for (const part of chunk(rows, WRITE_BATCH)) {
          const res = await sdk.table('ledger_c').create(part);
          entries += res?.data?.length ?? 0;
          (res?.messages ?? []).forEach((m) => errors.push(`Ledger: ${m}`));
        }
      }

      const skipped = classified.length - toCreate.length - toUpdate.length;
      setResult({ customers, created, updated, payments, entries, skipped, errors });
      if (created || updated || customers) {
        toast.success(
          `Imported ${created} order${created === 1 ? '' : 's'}${updated ? `, updated ${updated}` : ''}${
            customers ? `, added ${customers} customer${customers === 1 ? '' : 's'}` : ''
          }`,
        );
        logActivity(
          'Order',
          `Imported ${created} order${created === 1 ? '' : 's'}`,
          source?.name ?? 'Import',
          `${updated} updated · ${skipped} skipped · ${customers} new customer${customers === 1 ? '' : 's'}`,
        );
      } else {
        toast.error(errors[0] || 'Nothing was imported');
      }
    } catch (err) {
      toast.error('The import stopped part way', { description: err?.message });
      setResult({ customers, created, updated, payments, entries, skipped: 0, errors: [...errors, err?.message || 'Stopped'] });
    } finally {
      setImporting(false);
    }
  }

  const columns = [
    { label: 'Row', className: 'w-12 text-muted-foreground tabular-nums', render: (r) => r.line },
    {
      label: 'Client',
      render: (r) => (
        <div className="min-w-0">
          <p className="font-medium">{r.payload.full_name_c || <Dash />}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{r.payload.line_username_c || r.payload.email_c || '—'}</p>
          {r.state !== 'Problem' &&
            (r.customer ? (
              <p className="text-xs text-muted-foreground">Existing customer</p>
            ) : newCustomerKeys.has(r.ckey) ? (
              <p className="text-xs text-highlight">New customer</p>
            ) : null)}
        </div>
      ),
    },
    {
      label: 'Package',
      className: 'hidden md:table-cell',
      render: (r) =>
        r.payload.plan_c ? (
          <span>
            {planName(r.payload.plan_c)}
            <span className="block text-xs text-muted-foreground">
              {termLabel(r.payload.plan_c, r.payload.term_c)} · {r.payload.connections_c}{' '}
              {r.payload.plan_c === 'Telly' ? 'licence' : 'conn.'}
            </span>
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Term ends',
      className: 'hidden lg:table-cell',
      render: (r) => (r.payload.ends_at_c ? formatLocalDate(r.payload.ends_at_c, DATE_FORMATS.SHORT) : <Dash />),
    },
    {
      label: 'Paid',
      className: 'hidden sm:table-cell',
      render: (r) => (
        <span className="tabular-nums">
          {money(r.payload.price_c)}
          <span className="block text-xs text-muted-foreground">
            {r.paid ? paymentLabel(r.payload.payment_method_c) || 'Paid' : 'Not paid'}
          </span>
        </span>
      ),
    },
    {
      label: 'Result',
      render: (r) => (
        <div className="space-y-1">
          <StatusBadge value={r.action} styles={RESULT_STYLES} />
          {r.match && <p className="text-xs text-muted-foreground">Matches {r.match.Name}</p>}
          {r.problems.map((p) => (
            <p key={p} className="text-xs text-destructive">
              {p}
            </p>
          ))}
          {!r.problems.length &&
            r.warnings.map((w) => (
              <p key={w} className="text-xs text-warning">
                {w}
              </p>
            ))}
        </div>
      ),
    },
  ];

  if (result) {
    return (
      <div className="mx-auto max-w-4xl">
        <BackLink to="/admin/orders" label="Back to Orders" />
        <PageHeader eyebrow="Import" title="Import finished" description={`From ${source?.name ?? 'your file'}.`} />
        <div className="grid gap-3 sm:grid-cols-3">
          {withCustomers && <Metric label="Customers added" value={result.customers} icon="UserPlus" />}
          <Metric label="Orders added" value={result.created} icon="PackagePlus" />
          <Metric label="Orders updated" value={result.updated} icon="RefreshCw" />
          <Metric label="Rows skipped" value={result.skipped} icon="SkipForward" />
          {withPayments && <Metric label="Payments recorded" value={result.payments} icon="Banknote" />}
          {withLedger && <Metric label="Ledger entries" value={result.entries} icon="BookOpenCheck" />}
        </div>
        {result.errors.length > 0 && (
          <Panel title="What didn't go in" icon="TriangleAlert" className="mt-6">
            <ul className="space-y-1 py-2 text-sm text-destructive">
              {result.errors.slice(0, 30).map((e, i) => (
                <li key={`${i}-${e}`}>{e}</li>
              ))}
            </ul>
          </Panel>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/admin/orders">
              <ApperIcon name="Package" size={16} />
              View orders
            </Link>
          </Button>
          {result.customers > 0 && (
            <Button variant="outline" asChild>
              <Link to="/admin/customers">
                <ApperIcon name="Users" size={16} />
                View customers
              </Link>
            </Button>
          )}
          {withLedger && result.entries > 0 && (
            <Button variant="outline" asChild>
              <Link to="/admin/ledger">
                <ApperIcon name="BookOpenCheck" size={16} />
                Open the ledger
              </Link>
            </Button>
          )}
          <Button variant="ghost" onClick={reset}>
            <ApperIcon name="Upload" size={16} />
            Import another file
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl pb-24">
      <BackLink to="/admin/orders" label="Back to Orders" />
      <PageHeader
        eyebrow="Customers and orders"
        title="Import clients"
        description="Bring clients over from a spreadsheet. Each row becomes an order, and clients who are not here yet become customers. CSV, XLS and XLSX all work. Nothing is saved until you press Import."
        actions={
          <>
            <Button size="sm" onClick={templateXlsx}>
              <ApperIcon name="FileDown" size={16} />
              Excel template
            </Button>
            <Button variant="outline" size="sm" onClick={downloadTemplateCsv}>
              <ApperIcon name="FileText" size={16} />
              CSV template
            </Button>
            <Button variant="ghost" size="sm" onClick={sampleXlsx}>
              <ApperIcon name="FileSpreadsheet" size={16} />
              Filled-in example
            </Button>
          </>
        }
      />

      {!source ? (
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <label
            htmlFor="import-file"
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              openFile(e.dataTransfer.files?.[0]);
            }}
            className={`flex min-h-64 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
              dragging ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/60'
            }`}
          >
            <input
              ref={inputRef}
              id="import-file"
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={(e) => openFile(e.target.files?.[0])}
            />
            <span className="flex size-14 items-center justify-center rounded-xl bg-primary/10">
              <ApperIcon name={reading ? 'LoaderCircle' : 'Upload'} size={28} className={reading ? 'animate-spin text-primary' : 'text-primary'} />
            </span>
            <p className="mt-4 font-heading text-xl">{reading ? 'Reading the file…' : 'Drop a spreadsheet here'}</p>
            <p className="mt-1 text-sm text-muted-foreground">or click to choose one · .csv, .xls, .xlsx up to 10 MB</p>
          </label>

          <Panel title="Start from the template" icon="FileSpreadsheet" bodyClassName="space-y-4 py-4">
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-medium text-primary">1</span>
                <span>Download the Excel template. Every column is already set up.</span>
              </li>
              <li className="flex gap-2">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-medium text-primary">2</span>
                <span>
                  Fill in the <span className="font-medium text-foreground">Clients</span> sheet, one client per row. Only Client name and Service
                  are required.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-medium text-primary">3</span>
                <span>Save it and drop it here. Every row is checked before anything is saved.</span>
              </li>
            </ol>
            <div className="flex flex-wrap gap-2">
              <Button onClick={templateXlsx}>
                <ApperIcon name="FileDown" size={16} />
                Download Excel template
              </Button>
              <Button variant="outline" onClick={loadDemo}>
                <ApperIcon name="Play" size={16} />
                Try the demo
              </Button>
            </div>
            <div className="border-t border-border pt-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">What's in the template</p>
              <ul className="mt-1 space-y-1 leading-relaxed">
                <li>
                  <span className="text-foreground">Clients</span> · the blank sheet you fill in, with all {IMPORT_COLUMNS.length} columns.
                </li>
                <li>
                  <span className="text-foreground">How to fill it in</span> · what each column means, what it accepts, and an example.
                </li>
                <li>
                  <span className="text-foreground">Lists</span> · the exact services, terms, statuses and payment methods.
                </li>
                <li>
                  <span className="text-foreground">Example</span> · three made-up clients filled in. Only Clients is imported.
                </li>
              </ul>
            </div>
          </Panel>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <ApperIcon name={source.demo ? 'Sparkles' : 'FileSpreadsheet'} size={20} className="shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="truncate font-medium">{source.name}</p>
                <p className="text-xs text-muted-foreground">
                  {sheetRows.length} row{sheetRows.length === 1 ? '' : 's'} · {matched} of {headers.length} columns matched
                  {checking ? ' · checking for existing orders…' : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {source.sheets.length > 1 && (
                <PickSelect
                  value={String(sheetIndex)}
                  onChange={(v) => setSheetIndex(Number(v))}
                  values={source.sheets.map((s, i) => String(i))}
                  labels={Object.fromEntries(source.sheets.map((s, i) => [String(i), `Sheet: ${s.name}`]))}
                  className="sm:w-48"
                />
              )}
              <Button variant="ghost" size="sm" onClick={reset} disabled={importing}>
                <ApperIcon name="X" size={16} />
                Choose another file
              </Button>
            </div>
          </div>

          {source.demo && (
            <div className="flex gap-3 rounded-lg border border-warning-border bg-warning-muted px-4 py-3 text-sm text-warning">
              <ApperIcon name="FlaskConical" size={18} className="mt-0.5 shrink-0" />
              <p>These are made-up clients. Importing them adds real orders to your store, so delete them afterwards or just look around.</p>
            </div>
          )}

          <Panel title="Columns" icon="Columns3" bodyClassName="py-4">
            {missingRequired.length > 0 && (
              <p className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                Pick a column for {missingRequired.map((c) => c.label).join(' and ')} to continue.
              </p>
            )}
            <div className="grid gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-3">
              {COLUMN_GROUPS.map((group) => (
                <div key={group} className="space-y-2.5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{group}</p>
                  {IMPORT_COLUMNS.filter((c) => c.group === group).map((c) => (
                    <div key={c.key} className="grid grid-cols-[7.5rem_1fr] items-center gap-2">
                      <label htmlFor={`map-${c.key}`} className="text-sm">
                        {c.label}
                        {c.required && <span className="text-destructive"> *</span>}
                      </label>
                      <ColumnSelect
                        id={`map-${c.key}`}
                        value={map[c.key]}
                        headers={headers}
                        sample={sheetRows[0]}
                        onChange={(v) =>
                          setMap((m) => {
                            const next = { ...m };
                            if (v === undefined) delete next[c.key];
                            else next[c.key] = v;
                            return next;
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {unused.length > 0 && (
              <p className="mt-4 text-xs text-muted-foreground">Not imported: {unused.filter(Boolean).join(', ') || 'blank headers'}</p>
            )}
          </Panel>

          <Panel title="Options" icon="SlidersHorizontal" bodyClassName="grid gap-4 py-4 md:grid-cols-2">
            <Field
              label="Line server for every row"
              htmlFor="import-server"
              hint={map.server !== undefined ? 'Used only where the file leaves the server blank.' : 'The file has no server column. Leave blank to add it later.'}
            >
              <Input
                id="import-server"
                value={server}
                onChange={(e) => setServer(e.target.value)}
                placeholder="http://line.example.com:8080"
              />
            </Field>
            <Field label="When a line username is already here" htmlFor="import-dups" hint="Matched on line username, or order code when the file has one.">
              <PickSelect id="import-dups" value={dupMode} onChange={setDupMode} values={DUP_MODES} labels={DUP_LABELS} />
            </Field>
            <SwitchRow
              id="import-customers"
              label="Create customers for new clients"
              hint="Matched on email, or on name when a row has no email. Every order is linked to its customer."
              checked={withCustomers}
              onChange={setWithCustomers}
            />
            <SwitchRow
              id="import-payments"
              label="Record a payment for paid rows"
              hint="One payment per new order, dated the start date, for the price in the file."
              checked={withPayments}
              onChange={setWithPayments}
            />
            <SwitchRow
              id="import-ledger"
              label="Add ledger entries"
              hint="A New line entry per new order with Collected filled in. Cost comes from the Cost column, or from credits x the credit rate."
              checked={withLedger}
              onChange={setWithLedger}
            />
          </Panel>

          <Panel
            title="Preview"
            icon="ListChecks"
            bodyClassName="py-4"
            actions={<span className="text-xs text-muted-foreground">{checking ? 'Checking…' : `${counts.Ready} ready`}</span>}
          >
            <div className="mb-4 flex flex-wrap gap-2">
              {VIEWS.map((v) => (
                <Pill key={v} on={view === v} onClick={() => setView(v)}>
                  {VIEW_LABELS[v]} · {counts[v]}
                </Pill>
              ))}
            </div>
            {visible.length ? (
              <>
                <DataTable columns={columns} rows={pageRows} />
                <Pager
                  page={page}
                  total={visible.length}
                  pageSize={PAGE_SIZE}
                  hasMore={page * PAGE_SIZE < visible.length}
                  onPage={setPage}
                />
              </>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">No rows in this view.</p>
            )}
          </Panel>

          <div className="sticky bottom-4 z-10 flex flex-col gap-3 rounded-lg border border-border bg-card/95 px-4 py-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1 text-sm">
              {importing ? (
                <div className="space-y-1.5">
                  <p>
                    Importing {progress.done} of {progress.total}…
                  </p>
                  <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
                </div>
              ) : (
                <p className="text-muted-foreground">
                  {toCreate.length} new
                  {toUpdate.length ? ` · ${toUpdate.length} to update` : ''}
                  {newCustomers.length ? ` · ${newCustomers.length} new customer${newCustomers.length === 1 ? '' : 's'}` : ''}
                  {counts.Duplicate && dupMode === 'skip' ? ` · ${counts.Duplicate} skipped` : ''}
                  {counts.Problem ? ` · ${counts.Problem} with problems (left out)` : ''}
                </p>
              )}
            </div>
            <Button onClick={runImport} disabled={importing || checking || !writes || missingRequired.length > 0}>
              <Busy
                busy={importing}
                label={
                  <>
                    <ApperIcon name="Download" size={16} />
                    Import {writes} order{writes === 1 ? '' : 's'}
                  </>
                }
                busyLabel="Importing…"
              />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
